// SCREENS-S1 in the in-memory API (docs/design/2026-10-28-screens-stack-contract.md §4,
// browser-computer decision §7): the Screens tab's routes (`screens.*`), the company's browser
// logins (`browserAccounts.*`) and the agent's lease/act/release (`agentScreens.*`), with the
// real routes' rules in small — watch for every member; take over, log in and manage a login for
// its owner or an admin; bump for admins; one controller at a time.
//
// Synthetic data only, and only for the demo company (Acme): its stack, Riley's browser on
// `instagram/demo`, an agent's desktop, Sam waiting on the owner's LinkedIn login (Needs you) and
// two waiting screens. Every other company has no stack (the tab's honest empty state).
// SCREENS-NEEDS-YOU: Sam's login wait is also an open ask (`screens.ask`), whose card the chat
// twin posts in #recruiting; hand back resolves it "done", not now "declined", a stop "ended".
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, json, notFound, type Principal, problem, validation } from "./fake";
import { COMPANY_IDS, type FakeDe, SCREEN_ASK_ID, uid } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";

type Overview = OperationOutput<"screens.overview">;
type LeaseView = Overview["live"][number];
type StackView = NonNullable<Overview["stack"]>;
type AccountView = OperationOutput<"browserAccounts.list">["data"][number];
type GrantView = AccountView["grants"][number];
type AskView = OperationOutput<"screens.ask">;
type Who = { membership_id: string; name: string };

export const FAKE_SCREENS_ROUTES = [
  "screens.overview",
  "screens.open",
  "screens.ticket",
  "screens.live",
  "screens.frame",
  "screens.browse",
  "screens.takeOver",
  "screens.handBack",
  "screens.ask",
  "screens.askNotNow",
  "screens.askRestart",
  "screens.askExtend",
  "screens.bump",
  "screens.stop",
  "browserAccounts.list",
  "browserAccounts.create",
  "browserAccounts.revoke",
  "browserAccounts.login",
  "browserAccounts.grant",
  "browserAccounts.ungrant",
  "browserAccounts.update",
  "agentScreens.lease",
  "agentScreens.act",
  "agentScreens.needsYou",
  "agentScreens.release",
  "agentScreens.profiles",
] as const satisfies readonly RouteId[];

/** A 1×1 grey PNG: the twin's screen picture. */
export const FAKE_FRAME_PNG =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";

const HANDLE = /^[a-z0-9][a-z0-9._-]{0,39}\/[a-z0-9][a-z0-9._-]{0,39}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Lease = {
  id: string;
  kind: LeaseView["kind"];
  state: LeaseView["state"];
  holder: LeaseView["holder"];
  task: LeaseView["task"];
  accountId: string | null;
  origin: LeaseView["origin"];
  priority: number;
  reason: string | null;
  title: string | null;
  queuedAt: string;
  startedAt: string | null;
  lastActivityAt: string | null;
  controller: Who | null;
  needsYou: LeaseView["needs_you"];
  handBackNote: string | null;
  /** The hand-back note the agent has not read yet (`agentScreens.act` answers it once). */
  unreadNote: string | null;
  /** A ClawPanel operator has control now; operators watching (controllers included). */
  operatorControl?: boolean;
  operatorViewing?: number;
  /** A browser's pages, last one shown (the address bar: navigate pushes, back pops). */
  pages?: string[];
};

type Account = {
  id: string;
  handle: string;
  /** PERSISTENT-SCREENS: a browser's site login or a desktop's home (no site). */
  kind?: AccountView["kind"];
  origin: string | null;
  createdBy?: AccountView["created_by"];
  label: string;
  /** SAVED-LOGIN-SHARING: when to use it (DEs pick between several logins by it). */
  useFor?: string | null;
  status: AccountView["status"];
  owner: Who;
  grants: GrantView[];
  lastUsedAt: string | null;
  createdAt: string;
};

/** A needs-you ask (its owner is the account's owner; admins only without an account). */
type Ask = {
  id: string;
  leaseId: string;
  state: AskView["state"];
  why: AskView["why"];
  reason: string | null;
  agent: AskView["agent"];
  accountId: string | null;
  expiresAt: string;
  resolvedBy: Who | null;
  resolvedAt: string | null;
  createdAt: string;
  /** SCREEN-HANDOFF: when "Start a new screen" asked the agent again. */
  restartAt?: string | null;
  /** PERSISTENT-SCREENS: Extend presses. */
  extensions?: number;
};

type Stack = Omit<StackView, "capacity"> & {
  browsersMax: number;
  desktopsMax: number;
  /** The stack answered the last reconciliation. */
  reachable: boolean;
};
type OperatorScreen = Overview["operator"][number];
type State = {
  /** Null: the company has no computer yet (logins can still be added; they wait for it). */
  stack: Stack | null;
  leases: Lease[];
  accounts: Account[];
  /** Screens the ClawPanel operator started from its own page (same capacity, read-only here). */
  operator: OperatorScreen[];
  /** Needs-you asks, as their chat cards read them. */
  asks: Ask[];
};
/** A company with a stack. */
type Live = State & { stack: Stack };
const hasStack = (s: State): s is Live => s.stack !== null;

type Person = Who & { roles: string[] };

export function createScreensFake(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  peopleOf: (companyId: string) => Person[];
  /** A seat's title (grants name a seat), or null when the company has no such seat. */
  seatTitle: (p: Principal, seatId: string) => string | null;
}) {
  const states = new Map<string, State>();
  let seq = 0;
  const nextId = () => uid(0x5e20, ++seq);
  const iso = (minutesAgo = 0) =>
    new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();
  const empty = (): State => ({ stack: null, leases: [], accounts: [], operator: [], asks: [] });

  /** Acme's seeded stack and screens; no stack for every other company. */
  const seed = (companyId: string): State => {
    if (companyId !== COMPANY_IDS.acme) return empty();
    const des = deps.desOf(companyId);
    const people = deps.peopleOf(companyId);
    const owner = people.find((p) => p.roles.includes("owner")) ?? people[0];
    const member = people.find((p) => p.roles.includes("member")) ?? owner;
    const riley = des.find((d) => d.display_name === "Riley") ?? des[0];
    const sam = des.find((d) => d.display_name === "Sam") ?? des[1] ?? riley;
    if (!owner || !member || !riley || !sam) return empty();
    const who = (p: Person): Who => ({ membership_id: p.membership_id, name: p.name });
    const insta: Account = {
      id: uid(0x5e10, 1),
      handle: "instagram/demo",
      origin: "https://www.instagram.com",
      label: "Acme Instagram",
      useFor: "Brand posts and DMs",
      status: "live",
      owner: who(member),
      grants: [
        {
          id: uid(0x5e11, 1),
          subject_kind: "membership",
          subject_id: riley.membership_id,
          subject_name: riley.display_name,
          level: "use_act",
          granted_by: who(member),
          created_at: iso(60 * 24 * 3),
        },
      ],
      lastUsedAt: iso(2),
      createdAt: iso(60 * 24 * 7),
    };
    const linkedin: Account = {
      id: uid(0x5e10, 2),
      handle: "linkedin/maya",
      origin: "https://www.linkedin.com",
      label: "Maya's LinkedIn",
      status: "needs_you",
      owner: who(owner),
      grants: [
        {
          id: uid(0x5e11, 2),
          subject_kind: "seat",
          subject_id: sam.seat_id,
          subject_name: sam.seat_title,
          level: "use_read",
          granted_by: who(owner),
          created_at: iso(60 * 24 * 2),
        },
      ],
      lastUsedAt: iso(5),
      createdAt: iso(60 * 24 * 5),
    };
    // SAVED-LOGIN-SHARING: two more Instagram logins, each with its "use for" and its shares;
    // the client's was logged in on Sam's screen and shared on by the owner, and has expired.
    const careers: Account = {
      id: uid(0x5e10, 3),
      handle: "instagram/careers",
      origin: "https://www.instagram.com",
      label: "Acme Careers",
      useFor: "Job ads, Mon–Wed",
      status: "live",
      owner: who(owner),
      grants: [
        {
          id: uid(0x5e11, 3),
          subject_kind: "membership",
          subject_id: riley.membership_id,
          subject_name: riley.display_name,
          level: "use_act",
          granted_by: who(owner),
          created_at: iso(60 * 24 * 4),
        },
        {
          id: uid(0x5e11, 4),
          subject_kind: "membership",
          subject_id: sam.membership_id,
          subject_name: sam.display_name,
          level: "use_read",
          granted_by: who(owner),
          created_at: iso(60 * 24 * 4),
        },
      ],
      lastUsedAt: iso(60 * 20),
      createdAt: iso(60 * 24 * 4),
    };
    const client: Account = {
      id: uid(0x5e10, 4),
      handle: "instagram/northwind",
      origin: "https://www.instagram.com",
      label: "Northwind (client)",
      useFor: "Client posts, Thu–Fri",
      status: "expired",
      owner: who(owner),
      createdBy: { membership_id: sam.membership_id, name: sam.display_name, kind: "agent" },
      grants: [
        {
          id: uid(0x5e11, 5),
          subject_kind: "membership",
          subject_id: sam.membership_id,
          subject_name: sam.display_name,
          level: "use_act",
          granted_by: who(owner),
          created_at: iso(60 * 24 * 2),
        },
      ],
      lastUsedAt: iso(60 * 24 * 2),
      createdAt: iso(60 * 24 * 6),
    };
    const lease = (
      n: number,
      over: Partial<Lease> & Pick<Lease, "kind" | "state" | "holder">,
    ): Lease => ({
      id: uid(0x5e12, n),
      task: null,
      accountId: null,
      origin: "agent",
      priority: 0,
      reason: null,
      title: null,
      queuedAt: iso(30),
      startedAt: over.state === "queued" ? null : iso(25),
      lastActivityAt: over.state === "queued" ? null : iso(1),
      controller: null,
      needsYou: null,
      handBackNote: null,
      unreadNote: null,
      ...over,
    });
    const de = (d: FakeDe) => ({
      membership_id: d.membership_id,
      name: d.display_name,
      kind: "de" as const,
    });
    const ag = { membership_id: uid(0x5e13, 1), name: "Lead researcher", kind: "ag" as const };
    // Sam's login wait (lease 3) as its chat card reads it: open, an hour left.
    const ask: Ask = {
      id: SCREEN_ASK_ID,
      leaseId: uid(0x5e12, 3),
      state: "open",
      why: "login",
      reason: "shortlist candidates for the SDR role",
      agent: de(sam),
      accountId: linkedin.id,
      expiresAt: iso(-60),
      resolvedBy: null,
      resolvedAt: null,
      createdAt: iso(5),
    };
    return {
      asks: [ask],
      stack: {
        name: "Acme computer",
        state: "live",
        placement: "shared",
        browser_live: "vnc",
        health: { browsers: "ok", desktops: "ok", live: "ok" },
        last_seen_at: iso(0),
        // Full: the two browsers and both desktops (the agent's, the operator's) are in use,
        // so two screens wait.
        browsersMax: 2,
        desktopsMax: 2,
        reachable: true,
      },
      accounts: [insta, careers, client, linkedin],
      operator: [
        {
          lease_id: "op-desktop-1",
          kind: "desktop",
          name: "Maintenance desktop",
          started_at: iso(12),
          operator_control: true,
          operator_viewing: 1,
        },
      ],
      leases: [
        lease(1, {
          kind: "browser",
          state: "active",
          holder: de(riley),
          task: { id: uid(0x5e14, 1), title: "Reply to new Instagram DMs" },
          accountId: insta.id,
          title: "Instagram · Inbox",
        }),
        lease(2, {
          kind: "desktop",
          state: "active",
          holder: ag,
          task: { id: uid(0x5e14, 2), title: "Collect 20 leads from the trade-show list" },
          title: "LibreOffice Calc",
          // The operator is helping out on this one.
          operatorControl: true,
          operatorViewing: 2,
        }),
        lease(3, {
          kind: "browser",
          state: "needs_you",
          holder: de(sam),
          task: { id: uid(0x5e14, 3), title: "Shortlist candidates for the SDR role" },
          accountId: linkedin.id,
          title: "LinkedIn · Sign in",
          needsYou: { why: "login", note: "The session expired; please log in." },
        }),
        lease(4, {
          kind: "browser",
          state: "queued",
          holder: de(riley),
          task: { id: uid(0x5e14, 4), title: "Post the weekly job ad" },
          accountId: insta.id,
          queuedAt: iso(8),
        }),
        lease(5, {
          kind: "desktop",
          state: "queued",
          holder: de(sam),
          task: { id: uid(0x5e14, 5), title: "Export the pipeline report" },
          queuedAt: iso(4),
        }),
      ],
    };
  };

  const stateOf = (companyId: string): State => {
    let s = states.get(companyId);
    if (!s) {
      s = seed(companyId);
      states.set(companyId, s);
    }
    return s;
  };
  /** The company's screens, when it has a computer. */
  const liveOf = (companyId: string): Live | null => {
    const s = stateOf(companyId);
    return hasStack(s) ? s : null;
  };

  const meOf = (ctx: Ctx, p: Principal): Who | null => {
    const m = ctx.me.memberships.find((x) => x.company_id === p.company.id && x.kind === "person");
    return m ? { membership_id: m.membership_id, name: ctx.me.user.name } : null;
  };
  /** Owner or admin of the company, or an agency operator acting for it. */
  const isAdmin = (p: Principal) =>
    p.operator || p.roles.includes("owner") || p.roles.includes("admin");

  const accountOf = (s: State, id: string | null) =>
    id ? (s.accounts.find((a) => a.id === id) ?? null) : null;
  const isLive = (l: Lease) => l.state !== "queued" && l.state !== "ended";

  /** The Waiting order: highest priority, person-opened first, then the oldest. */
  const waiting = (s: State) =>
    s.leases
      .filter((l) => l.state === "queued")
      .sort(
        (a, b) =>
          b.priority - a.priority ||
          (a.origin === b.origin ? 0 : a.origin === "person" ? -1 : 1) ||
          a.queuedAt.localeCompare(b.queuedAt),
      );

  const mayControl = (s: State, l: Lease, p: Principal, me: Who | null) => {
    if (isAdmin(p)) return true;
    const acc = accountOf(s, l.accountId);
    if (acc) return acc.owner.membership_id === me?.membership_id;
    return l.holder.membership_id === me?.membership_id;
  };

  const leaseView = (s: Live, l: Lease, p: Principal, me: Who | null): LeaseView => {
    const acc = accountOf(s, l.accountId);
    const queue = waiting(s);
    const control = mayControl(s, l, p, me) && isLive(l);
    return {
      id: l.id,
      kind: l.kind,
      state: l.state,
      holder: l.holder,
      task: l.task,
      account: acc ? { id: acc.id, handle: acc.handle, label: acc.label } : null,
      origin: l.origin,
      priority: l.priority,
      reason: l.reason,
      title: l.title,
      queued_at: l.queuedAt,
      started_at: l.startedAt,
      last_activity_at: l.lastActivityAt,
      position: l.state === "queued" ? queue.indexOf(l) + 1 : null,
      controller: l.controller,
      needs_you: l.needsYou ? { ...l.needsYou, ...openAskTimer(s, l.id) } : null,
      hand_back_note: l.handBackNote,
      operator_control: l.operatorControl ?? false,
      operator_viewing: l.operatorViewing ?? 0,
      live_mode: l.kind === "browser" ? s.stack.browser_live : "vnc",
      can: {
        watch: isLive(l),
        control,
        bump: isAdmin(p) && l.state === "queued",
        stop:
          l.state !== "ended" &&
          (isAdmin(p) ||
            acc?.owner.membership_id === me?.membership_id ||
            l.holder.membership_id === me?.membership_id),
      },
    };
  };

  /** Browsers or desktops in use: our live screens and the operator's. */
  const used = (s: State, kind: Lease["kind"]) =>
    s.leases.filter((x) => isLive(x) && x.kind === kind).length +
    s.operator.filter((o) => o.kind === kind).length;

  const stackView = (s: Live): StackView => {
    const { browsersMax, desktopsMax, reachable: _r, ...rest } = s.stack;
    return {
      ...rest,
      capacity: {
        browsers_max: browsersMax,
        browsers_used: used(s, "browser"),
        desktops_max: desktopsMax,
        desktops_used: used(s, "desktop"),
      },
    };
  };

  /** PERSISTENT-SCREENS: the open ask on a lease (the Live countdown and Extend). */
  const openAskTimer = (s: State, leaseId: string) => {
    const a = s.asks.find((x) => x.leaseId === leaseId && x.state === "open");
    return { ask_id: a?.id ?? null, expires_at: a?.expiresAt ?? null };
  };

  /** SAVED-LOGIN-SHARING: who drives the login now (one at a time) and how many wait for it. */
  const loadOf = (a: Account) => {
    const s = [...states.values()].find((x) => x.accounts.includes(a));
    const on = s?.leases.filter((l) => l.accountId === a.id) ?? [];
    const live = on.find(isLive);
    return {
      in_use_by: live
        ? {
            membership_id: live.holder.membership_id,
            name: live.holder.name,
            kind: live.holder.kind,
            since: live.startedAt,
          }
        : null,
      waiting: on.filter((l) => l.state === "queued").length,
    };
  };

  const accountView = (a: Account, p: Principal, me: Who | null): AccountView => ({
    id: a.id,
    handle: a.handle,
    kind: a.kind ?? "browser",
    origin: a.origin,
    label: a.label,
    use_for: a.useFor ?? null,
    status: a.status,
    owner: a.owner,
    ...loadOf(a),
    created_by: a.createdBy ?? { ...a.owner, kind: "person" },
    grants: a.grants,
    last_used_at: a.lastUsedAt,
    created_at: a.createdAt,
    can_manage: isAdmin(p) || a.owner.membership_id === me?.membership_id,
  });

  /** Starts the waiting screens that now fit (after a stop or a release). */
  const admit = (s: State) => {
    if (!hasStack(s)) return;
    for (const l of waiting(s)) {
      const max = l.kind === "browser" ? s.stack.browsersMax : s.stack.desktopsMax;
      if (used(s, l.kind) < max) {
        l.state = "active";
        l.startedAt = iso();
        l.lastActivityAt = iso();
      }
    }
  };
  const fits = (s: Live, kind: Lease["kind"]) =>
    used(s, kind) < (kind === "browser" ? s.stack.browsersMax : s.stack.desktopsMax);

  /** A kind with max 0 is refused, never queued (it would wait forever). */
  const noSuchKind = (s: Live, kind: Lease["kind"]) =>
    (kind === "browser" ? s.stack.browsersMax : s.stack.desktopsMax) <= 0
      ? problem(
          409,
          "screens_kind_unavailable",
          "This company's computer has none of that kind",
          `This company's computer has no ${kind}s`,
        )
      : null;

  const leaseState = (detail: string) =>
    problem(409, "screens_lease_state", "Not possible in this state", detail);

  /** Why an agent waits instead of acting (queued, taken over, waiting on a person), else null. */
  const waitOf = (l: Lease) =>
    l.state === "queued" || l.state === "taken_over" || l.state === "needs_you" ? l.state : null;

  /** The caller, the company's screens and the lease `{id}`; or the refusal. */
  const withLease = (
    ctx: Ctx,
    run: (s: Live, l: Lease, p: Principal, me: Who | null) => Response,
  ): Response => {
    const p = ctx.principal;
    if (!p) return notFound();
    const s = liveOf(p.company.id);
    const l = s?.leases.find((x) => x.id === ctx.params.id);
    if (!s || !l) return notFound();
    return run(s, l, p, meOf(ctx, p));
  };

  const withAccount = (
    ctx: Ctx,
    run: (s: State, a: Account, p: Principal, me: Who | null) => Response,
  ): Response => {
    const p = ctx.principal;
    if (!p) return notFound();
    const s = stateOf(p.company.id);
    const a = s?.accounts.find((x) => x.id === ctx.params.id && x.status !== "revoked");
    if (!s || !a) return notFound();
    const me = meOf(ctx, p);
    if (!isAdmin(p) && a.owner.membership_id !== me?.membership_id)
      return problem(403, "forbidden", "Forbidden", "The login's owner or an admin only");
    return run(s, a, p, me);
  };

  /** The account's owner or an admin opens an ask (admins only without an account). */
  const mayOpen = (s: State, a: Ask, p: Principal, me: Who | null) =>
    isAdmin(p) || (!!me && accountOf(s, a.accountId)?.owner.membership_id === me.membership_id);

  /** An open ask past its time expires (the agent is told). */
  const settle = (a: Ask) => {
    if (a.state === "open" && a.expiresAt < iso()) {
      a.state = "expired";
      a.resolvedAt = a.expiresAt;
    }
  };

  /** The lease's open asks resolve: handed back (done) or its screen ended. */
  const resolveAsks = (s: State, leaseId: string, state: Ask["state"], by: Who | null) => {
    for (const a of s.asks) {
      settle(a);
      if (a.leaseId !== leaseId || a.state !== "open") continue;
      a.state = state;
      a.resolvedBy = by;
      a.resolvedAt = iso();
    }
  };

  const askView = (s: State, a: Ask, p: Principal, me: Who | null): AskView => {
    settle(a);
    const l = s.leases.find((x) => x.id === a.leaseId);
    const acc = accountOf(s, a.accountId);
    return {
      id: a.id,
      lease_id: a.leaseId,
      state: a.state,
      why: a.why,
      reason: a.reason,
      agent: a.agent,
      account: acc ? { id: acc.id, handle: acc.handle, label: acc.label } : null,
      owner: acc ? acc.owner : null,
      expires_at: a.expiresAt,
      resolved_by: a.resolvedBy,
      resolved_at: a.resolvedAt,
      created_at: a.createdAt,
      can_open: a.state === "open" && !!l && isLive(l) && mayOpen(s, a, p, me),
      lease_state: l?.state ?? "ended",
      // SCREEN-HANDOFF: who drives it now, and "Start a new screen" once it is over.
      controller: a.state === "open" ? (l?.controller ?? null) : null,
      agent_active: true,
      restart_requested_at: a.restartAt ?? null,
      can_restart:
        (a.state === "expired" || a.state === "ended" || a.state === "declined") &&
        !a.restartAt &&
        mayOpen(s, a, p, me),
      message_id: a.id,
      extensions: a.extensions ?? 0,
      can_extend: canExtend(s, a, p, me),
    };
  };

  /** PERSISTENT-SCREENS: Extend (+15 min), at most 8 h after the ask, 16 times. */
  const canExtend = (s: State, a: Ask, p: Principal, me: Who | null) => {
    const l = s.leases.find((x) => x.id === a.leaseId);
    const capped =
      new Date(a.expiresAt).getTime() >= new Date(a.createdAt).getTime() + 8 * 3_600_000;
    return (
      a.state === "open" &&
      !capped &&
      (a.extensions ?? 0) < 16 &&
      (mayOpen(s, a, p, me) || (!!me && l?.controller?.membership_id === me.membership_id))
    );
  };

  const withAsk = (
    ctx: Ctx,
    run: (s: State, a: Ask, p: Principal, me: Who | null) => Response,
  ): Response => {
    const p = ctx.principal;
    if (!p) return notFound();
    const s = stateOf(p.company.id);
    const a = s.asks.find((x) => x.id === ctx.params.id);
    if (!a) return notFound();
    return run(s, a, p, meOf(ctx, p));
  };

  /** A browser or desktop a person opens (on one of their logins): they drive it once it starts. */
  const personLease = (
    s: Live,
    me: Who,
    kind: Lease["kind"],
    acc: Account | null,
    reason: string | null,
  ): Lease => {
    const start = fits(s, kind);
    const l: Lease = {
      id: nextId(),
      kind,
      state: start ? "active" : "queued",
      holder: { ...me, kind: "person" },
      task: null,
      accountId: acc?.id ?? null,
      origin: "person",
      priority: 0,
      reason,
      title: acc ? acc.label : null,
      queuedAt: iso(),
      startedAt: start ? iso() : null,
      lastActivityAt: start ? iso() : null,
      controller: start ? me : null,
      needsYou: null,
      handBackNote: null,
      unreadNote: null,
    };
    s.leases.push(l);
    if (acc) acc.lastUsedAt = iso();
    return l;
  };

  const note = (body: unknown): string | null | Response => {
    const raw = (body as { note?: unknown } | undefined)?.note;
    if (raw === undefined) return null;
    if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 280)
      return validation("note: 1–280 characters");
    return raw.trim();
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "screens.overview": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = liveOf(p.company.id);
      if (!s)
        return json({
          stack: null,
          live: [],
          waiting: [],
          needs_you: [],
          operator: [],
          stack_reachable: false,
          can_bump: false,
        } satisfies Overview);
      const me = meOf(ctx, p);
      const view = (l: Lease) => leaseView(s, l, p, me);
      return json({
        stack: stackView(s),
        live: s.leases.filter(isLive).map(view),
        waiting: waiting(s).map(view),
        needs_you: s.leases
          .filter(
            (l) =>
              l.state === "needs_you" &&
              (isAdmin(p) || accountOf(s, l.accountId)?.owner.membership_id === me?.membership_id),
          )
          .map(view),
        operator: s.operator,
        stack_reachable: s.stack.reachable,
        can_bump: isAdmin(p),
      } satisfies Overview);
    },
    "screens.open": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = liveOf(p.company.id);
      if (!s) return problem(409, "screens_no_stack", "No screens", "The company has no stack");
      const me = meOf(ctx, p);
      if (!me) return problem(403, "forbidden", "Forbidden", "A person only");
      const body = (ctx.body ?? {}) as { kind?: unknown; account_id?: unknown; reason?: unknown };
      if (body.kind !== "browser" && body.kind !== "desktop")
        return validation("kind: browser or desktop");
      let acc: Account | null = null;
      if (body.account_id !== undefined) {
        if (body.kind !== "browser") return validation("account_id: browser only");
        acc = s.accounts.find((a) => a.id === body.account_id && a.status !== "revoked") ?? null;
        if (!acc || (!isAdmin(p) && acc.owner.membership_id !== me.membership_id))
          return problem(403, "screens_no_grant", "No access", "Not your login");
      }
      const reason = note({ note: body.reason });
      if (reason instanceof Response) return reason;
      const none = noSuchKind(s, body.kind);
      if (none) return none;
      const l = personLease(s, me, body.kind, acc, reason);
      return json(leaseView(s, l, p, me), 201);
    },
    "screens.ticket": (ctx) =>
      withLease(ctx, (s, l, p, me) => {
        const role = (ctx.body as { role?: unknown } | undefined)?.role;
        if (role !== "watch" && role !== "control") return validation("role: watch or control");
        if (!isLive(l)) return leaseState("The screen is not live");
        if (role === "control" && l.controller?.membership_id !== me?.membership_id)
          return problem(409, "screens_controlled", "Not yours to drive", "Take it over first");
        const view = leaseView(s, l, p, me);
        return json({
          url: `/v1/companies/${p.company.id}/screens/leases/${l.id}/live?ticket=fake.${role}.${l.id}`,
          expires_at: new Date(deps.now().getTime() + 60_000).toISOString(),
          role,
          live_mode: view.live_mode,
        });
      }),
    "screens.live": (ctx) =>
      withLease(ctx, (_s, l) => {
        if (!ctx.url.searchParams.get("ticket")) return validation("ticket: required");
        return isLive(l) ? json({ ok: true }) : leaseState("The screen is not live");
      }),
    "screens.frame": (ctx) =>
      withLease(ctx, (_s, l) =>
        isLive(l)
          ? json({ image_png_base64: FAKE_FRAME_PNG, at: iso() })
          : leaseState("The screen is not live"),
      ),
    "screens.browse": (ctx) =>
      withLease(ctx, (_s, l, _p, me) => {
        const b = (ctx.body ?? {}) as { action?: unknown; url?: unknown };
        if (!["location", "navigate", "back", "reload"].includes(String(b.action)))
          return validation("action: location, navigate, back or reload");
        if (l.kind !== "browser" || !isLive(l)) return leaseState("Not a live browser");
        if (l.controller?.membership_id !== me?.membership_id)
          return problem(409, "screens_controlled", "Not yours to drive", "Take it over first");
        l.pages ??= [l.accountId ? "https://www.example.com/" : "https://duckduckgo.com/"];
        if (b.action === "navigate") {
          if (typeof b.url !== "string" || !/^https?:\/\/\S+$/.test(b.url))
            return validation("url: an http(s) address");
          l.pages.push(b.url);
        } else if (b.action === "back" && l.pages.length > 1) l.pages.pop();
        return json({ url: l.pages[l.pages.length - 1] ?? null });
      }),
    "screens.takeOver": (ctx) =>
      withLease(ctx, (s, l, p, me) => {
        if (!isLive(l)) return leaseState("The screen is not live");
        if (!me || !mayControl(s, l, p, me))
          return problem(403, "forbidden", "Forbidden", "The login's owner or an admin only");
        if (l.controller && l.controller.membership_id !== me.membership_id)
          return problem(409, "screens_controlled", "Someone is driving", l.controller.name);
        l.controller = me;
        if (l.holder.kind !== "person") l.state = "taken_over";
        return json(leaseView(s, l, p, me));
      }),
    "screens.handBack": (ctx) =>
      withLease(ctx, (s, l, p, me) => {
        const n = note(ctx.body);
        if (n instanceof Response) return n;
        if (!isLive(l) || !l.controller) return leaseState("Nobody is driving");
        if (l.controller.membership_id !== me?.membership_id && !isAdmin(p))
          return problem(409, "screens_controlled", "Not yours", l.controller.name);
        l.controller = null;
        l.state = "active";
        l.needsYou = null;
        l.handBackNote = n;
        l.unreadNote = n;
        l.lastActivityAt = iso();
        const acc = accountOf(s, l.accountId);
        if (acc && acc.status === "needs_you") acc.status = "live";
        resolveAsks(s, l.id, "done", me);
        return json(leaseView(s, l, p, me));
      }),
    "screens.ask": (ctx) => withAsk(ctx, (s, a, p, me) => json(askView(s, a, p, me))),
    "screens.askNotNow": (ctx) =>
      withAsk(ctx, (s, a, p, me) => {
        settle(a);
        if (a.state !== "open") return leaseState("The ask is no longer open");
        if (!me || !mayOpen(s, a, p, me))
          return problem(403, "forbidden", "Forbidden", "The login's owner or an admin only");
        a.state = "declined";
        a.resolvedBy = me;
        a.resolvedAt = iso();
        // The agent is told at once: it stops waiting on a person.
        const l = s.leases.find((x) => x.id === a.leaseId);
        if (l?.state === "needs_you") {
          l.state = "active";
          l.needsYou = null;
        }
        return json(askView(s, a, p, me));
      }),
    // SCREEN-HANDOFF: an expired or ended ask asks its agent for a new screen (once).
    "screens.askRestart": (ctx) =>
      withAsk(ctx, (s, a, p, me) => {
        settle(a);
        if (a.state === "open" || a.state === "done")
          return leaseState("The ask is still open or was handed back");
        if (!me || !mayOpen(s, a, p, me))
          return problem(403, "forbidden", "Forbidden", "The person asked or an admin only");
        if (a.restartAt)
          return problem(409, "conflict", "Conflict", "A new screen was already asked for.");
        a.restartAt = iso();
        return json(askView(s, a, p, me));
      }),
    "screens.askExtend": (ctx) =>
      withAsk(ctx, (s, a, p, me) => {
        settle(a);
        if (a.state !== "open") return leaseState("The ask is not open");
        if (!canExtend(s, a, p, me))
          return problem(
            409,
            "conflict",
            "Conflict",
            "This request can't be kept open any longer.",
          );
        const body = (ctx.body ?? {}) as { minutes?: unknown };
        const min = typeof body.minutes === "number" ? body.minutes : 15;
        const cap = new Date(a.createdAt).getTime() + 8 * 3_600_000;
        const from = Math.max(new Date(a.expiresAt).getTime(), deps.now().getTime());
        a.expiresAt = new Date(Math.min(cap, from + min * 60_000)).toISOString();
        a.extensions = (a.extensions ?? 0) + 1;
        return json(askView(s, a, p, me));
      }),
    "screens.bump": (ctx) =>
      withLease(ctx, (s, l, p, me) => {
        if (!p.manager) return problem(403, "forbidden", "Forbidden", "Admins only");
        if (l.state !== "queued") return leaseState("Only a waiting screen moves");
        l.priority = Math.max(0, ...s.leases.map((x) => x.priority)) + 1;
        return json(leaseView(s, l, p, me));
      }),
    "screens.stop": (ctx) =>
      withLease(ctx, (s, l, p, me) => {
        if (!leaseView(s, l, p, me).can.stop)
          return l.state === "ended"
            ? leaseState("Already ended")
            : problem(403, "forbidden", "Forbidden", "Not yours to stop");
        l.state = "ended";
        l.controller = null;
        resolveAsks(s, l.id, "ended", null);
        admit(s);
        return json(leaseView(s, l, p, me));
      }),
    "browserAccounts.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = stateOf(p.company.id);
      const me = meOf(ctx, p);
      return json({
        data: s.accounts.filter((a) => a.status !== "revoked").map((a) => accountView(a, p, me)),
      });
    },
    "browserAccounts.create": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const me = meOf(ctx, p);
      if (!me) return problem(403, "forbidden", "Forbidden", "A person only");
      const body = (ctx.body ?? {}) as {
        handle?: unknown;
        kind?: unknown;
        origin?: unknown;
        label?: unknown;
        use_for?: unknown;
      };
      if (typeof body.handle !== "string" || !HANDLE.test(body.handle))
        return validation("handle: site/name, lowercase");
      const kind = body.kind === "desktop" ? "desktop" : "browser";
      if (kind === "desktop" && body.origin !== undefined)
        return validation("origin: a desktop profile has no site");
      if (kind === "browser") {
        if (typeof body.origin !== "string" || !body.origin.startsWith("https://"))
          return validation("origin: https only");
        try {
          new URL(body.origin);
        } catch {
          return validation("origin: not a URL");
        }
      }
      const label = typeof body.label === "string" ? body.label.trim() : "";
      if (!label || label.length > 80) return validation("label: 1–80 characters");
      // Logins can be added before the company's computer exists; they wait for it.
      const list = stateOf(p.company.id).accounts;
      if (list.some((a) => a.handle === body.handle && a.status !== "revoked"))
        return problem(409, "conflict", "Already added", body.handle);
      const a: Account = {
        id: nextId(),
        handle: body.handle,
        kind,
        origin: kind === "browser" ? (body.origin as string) : null,
        label,
        useFor: typeof body.use_for === "string" ? body.use_for.trim() || null : null,
        status: "needs_you",
        owner: me,
        grants: [],
        lastUsedAt: null,
        createdAt: iso(),
      };
      list.push(a);
      return json(accountView(a, p, me), 201);
    },
    "browserAccounts.revoke": (ctx) =>
      withAccount(ctx, (s, a, p, me) => {
        a.status = "revoked";
        a.grants = [];
        for (const l of s.leases)
          if (l.accountId === a.id && l.state !== "ended") {
            l.state = "ended";
            l.controller = null;
            resolveAsks(s, l.id, "ended", null);
          }
        admit(s);
        return json(accountView(a, p, me));
      }),
    "browserAccounts.login": (ctx) =>
      withAccount(ctx, (s, a, p, me) => {
        if (!hasStack(s))
          return problem(409, "screens_no_stack", "No screens", "The company has no stack");
        if (!me) return problem(403, "forbidden", "Forbidden", "A person only");
        const kind = a.kind ?? "browser";
        const none = noSuchKind(s, kind);
        if (none) return none;
        const l = personLease(s, me, kind, a, null);
        return json(leaseView(s, l, p, me), 201);
      }),
    "browserAccounts.grant": (ctx) =>
      withAccount(ctx, (_s, a, p, me) => {
        const body = (ctx.body ?? {}) as {
          subject_kind?: unknown;
          subject_id?: unknown;
          level?: unknown;
        };
        if (body.subject_kind !== "membership" && body.subject_kind !== "seat")
          return validation("subject_kind: membership or seat");
        if (typeof body.subject_id !== "string" || !UUID.test(body.subject_id))
          return validation("subject_id: a uuid");
        if (body.level !== "use_read" && body.level !== "use_act" && body.level !== "manage")
          return validation("level: use_read, use_act or manage");
        const name =
          body.subject_kind === "seat"
            ? deps.seatTitle(p, body.subject_id)
            : (deps.desOf(p.company.id).find((d) => d.membership_id === body.subject_id)
                ?.display_name ?? null);
        if (!name) return validation("subject_id: no such DE or seat");
        if (
          a.grants.some(
            (g) => g.subject_kind === body.subject_kind && g.subject_id === body.subject_id,
          )
        )
          return problem(409, "conflict", "Already granted", name);
        a.grants.push({
          id: nextId(),
          subject_kind: body.subject_kind,
          subject_id: body.subject_id,
          subject_name: name,
          level: body.level,
          granted_by: me,
          created_at: iso(),
        });
        return json(accountView(a, p, me));
      }),
    "browserAccounts.update": (ctx) =>
      withAccount(ctx, (_s, a, p, me) => {
        const body = (ctx.body ?? {}) as { label?: unknown; use_for?: unknown };
        if (body.label === undefined && body.use_for === undefined)
          return validation("nothing to change");
        if (body.label !== undefined) {
          const label = typeof body.label === "string" ? body.label.trim() : "";
          if (!label || label.length > 80) return validation("label: 1–80 characters");
          a.label = label;
        }
        if (body.use_for !== undefined) {
          if (body.use_for !== null && typeof body.use_for !== "string")
            return validation("use_for: text");
          const u = typeof body.use_for === "string" ? body.use_for.trim() : "";
          if (u.length > 280) return validation("use_for: at most 280 characters");
          a.useFor = u || null;
        }
        return json(accountView(a, p, me));
      }),
    "browserAccounts.ungrant": (ctx) =>
      withAccount(ctx, (_s, a, p, me) => {
        const before = a.grants.length;
        a.grants = a.grants.filter((g) => g.id !== ctx.params.grant);
        if (a.grants.length === before) return notFound();
        return json(accountView(a, p, me));
      }),
    // The agent's side. The twin has no run tokens: the signed-in caller stands in for the run,
    // and its lease is held by the company's first DE.
    // PERSISTENT-SCREENS: the saved profiles the company's (first) DE is lent.
    "agentScreens.profiles": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = stateOf(p.company.id);
      const de = deps.desOf(p.company.id)[0];
      if (!de) return problem(403, "run_not_owned", "No run", "No DE in this company");
      return json({
        data: s.accounts.flatMap((a) => {
          const g = a.grants.find(
            (x) => x.subject_kind === "membership" && x.subject_id === de.membership_id,
          );
          return a.status === "revoked" || !g
            ? []
            : [
                {
                  handle: a.handle,
                  kind: a.kind ?? "browser",
                  site: a.origin,
                  label: a.label,
                  use_for: a.useFor ?? null,
                  owner: a.owner.name,
                  status: a.status,
                  level: g.level,
                  in_use_by: loadOf(a).in_use_by?.name ?? null,
                  waiting: loadOf(a).waiting,
                  last_used_at: a.lastUsedAt,
                },
              ];
        }),
      });
    },
    "agentScreens.lease": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = liveOf(p.company.id);
      if (!s) return problem(409, "screens_no_stack", "No screens", "The company has no stack");
      const body = (ctx.body ?? {}) as { kind?: unknown; account?: unknown; task?: unknown };
      if (body.kind !== "browser" && body.kind !== "desktop")
        return validation("kind: browser or desktop");
      if (typeof body.task !== "string" || !UUID.test(body.task)) return validation("task: uuid");
      const de = deps.desOf(p.company.id)[0];
      if (!de) return problem(403, "run_not_owned", "No run", "No DE in this company");
      let acc: Account | null = null;
      if (body.account !== undefined) {
        const name = String(body.account).trim().toLowerCase();
        acc =
          s.accounts.find((a) => a.handle === name && a.status !== "revoked") ??
          // SAVED-LOGIN-SHARING: or by its name.
          s.accounts.find((a) => a.label.toLowerCase() === name && a.status !== "revoked") ??
          null;
        if (!acc) return problem(403, "screens_no_grant", "No access", String(body.account));
        // PERSISTENT-SCREENS: a profile not known to be signed in still opens (checked first).
      }
      const none = noSuchKind(s, body.kind);
      if (none) return none;
      const start = fits(s, body.kind);
      const l: Lease = {
        id: nextId(),
        kind: body.kind,
        state: start ? "active" : "queued",
        holder: { membership_id: de.membership_id, name: de.display_name, kind: "de" },
        task: { id: body.task, title: "Task" },
        accountId: acc?.id ?? null,
        origin: "agent",
        priority: 0,
        reason: null,
        title: null,
        queuedAt: iso(),
        startedAt: start ? iso() : null,
        lastActivityAt: start ? iso() : null,
        controller: null,
        needsYou: null,
        handBackNote: null,
        unreadNote: null,
      };
      s.leases.push(l);
      return json({
        lease_id: l.id,
        state: l.state,
        position: start ? null : waiting(s).indexOf(l) + 1,
        account: acc?.handle ?? null,
      });
    },
    "agentScreens.act": (ctx) =>
      withLease(ctx, (_s, l) => {
        const body = (ctx.body ?? {}) as { action?: unknown };
        if (typeof body.action !== "string") return validation("action: required");
        if (l.state === "ended") return leaseState("The screen has ended");
        const wait = waitOf(l);
        if (wait) return json({ state: l.state, wait, note: null, result: null });
        const read = l.unreadNote;
        l.unreadNote = null;
        l.lastActivityAt = iso();
        return json({
          state: l.state,
          wait: null,
          note: read,
          result:
            body.action === "screenshot" ? { image_png_base64: FAKE_FRAME_PNG } : { text: "ok" },
        });
      }),
    "agentScreens.needsYou": (ctx) =>
      withLease(ctx, (s, l) => {
        const body = (ctx.body ?? {}) as { why?: unknown; note?: unknown; wait_s?: unknown };
        if (!["login", "2fa", "captcha", "other"].includes(String(body.why)))
          return validation("why: login | 2fa | captcha | other");
        if (l.state === "ended")
          return json({ state: l.state, wait: "ended", note: null, result: null });
        const wait = waitOf(l);
        if (wait) return json({ state: l.state, wait, note: null, result: null });
        l.lastActivityAt = iso();
        l.state = "needs_you";
        l.needsYou = {
          why: body.why as "login",
          note: typeof body.note === "string" ? body.note : null,
        };
        // The ask its chat card reads (the API posts the card where the work came from).
        const waitS = typeof body.wait_s === "number" ? body.wait_s : 600;
        s.asks.push({
          id: nextId(),
          leaseId: l.id,
          state: "open",
          why: body.why as Ask["why"],
          reason: l.needsYou.note,
          agent: { ...l.holder, kind: l.holder.kind === "ag" ? "ag" : "de" },
          accountId: l.accountId,
          expiresAt: new Date(deps.now().getTime() + waitS * 1000).toISOString(),
          resolvedBy: null,
          resolvedAt: null,
          createdAt: iso(),
        });
        return json({ state: l.state, wait: "needs_you", note: null, result: null });
      }),
    "agentScreens.release": (ctx) =>
      withLease(ctx, (s, l) => {
        if (l.state !== "ended") {
          l.state = "ended";
          l.controller = null;
          resolveAsks(s, l.id, "ended", null);
          admit(s);
        }
        return json({
          lease_id: l.id,
          state: l.state,
          position: null,
          account: accountOf(s, l.accountId)?.handle ?? null,
        });
      }),
  };

  /** Test hook: the panel's maxima for a company that has a computer. */
  const setCapacity = (
    companyId: string,
    max: { browsers?: number; desktops?: number; browserLive?: "vnc" | "screenshot" },
  ) => {
    const s = liveOf(companyId);
    if (!s) return;
    if (max.browsers !== undefined) s.stack.browsersMax = max.browsers;
    if (max.desktops !== undefined) s.stack.desktopsMax = max.desktops;
    if (max.browserLive !== undefined) s.stack.browser_live = max.browserLive;
  };

  /**
   * CHAT-PRESENCE: what the chat twin's DE rings read: the open asks (whom each waits on: the
   * login's owner, or the admins without one) and the live screens by holder.
   */
  const presenceOf = (companyId: string) => {
    const s = stateOf(companyId);
    return {
      asks: s.asks
        // Open and not past its wait window (the API leaves expired asks out).
        .filter((a) => a.state === "open" && new Date(a.expiresAt) > deps.now())
        .map((a) => ({
          agent_membership_id: a.agent.membership_id,
          owner_membership_id: accountOf(s, a.accountId)?.owner.membership_id ?? null,
          lease_id: a.leaseId,
          why: a.why,
          reason: a.reason,
          created_at: a.createdAt,
        })),
      leases: s.leases
        .filter(isLive)
        .map((l) => ({ id: l.id, holder_membership_id: l.holder.membership_id })),
    };
  };

  return { handlers, stateOf, setCapacity, presenceOf };
}
